import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import cn from 'clsx';
import { debounce } from 'lodash';
import styles from './style.module.scss';
import Button from '@components-clean/core/button';
import CoreSplitter from '@components-clean/core/core-splitter';
import FeActionsBar from '@components-clean/core/filter-ui/action-bar/fe-action-bar';
import { FilterUIStateUpdate } from '@components-clean/core/filter-ui/action-bar/shared/interfaces';
import Toggle from '@components-clean/core/toggle';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import {
    IToggleToolkitEventBusMessage,
    TOGGLE_TOOLKIT,
    ToolkitStateValues,
} from '@pages-clean/central-monitoring/monitoring-toolkit/wrapper';
import { IRefreshGridEventBusMessage, REFRESH_GRID } from '@pages-clean/risk-assessment/constants';
import CmControlsNewDatasetInfoDialog from '@pages-clean/risk-assessment/risk-log/dialogs/cm-controls-new-dataset-info-dialog';
import { IFormData, RiskLogContext, SelectionState } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import RiskLogChildAreas from '@pages-clean/risk-assessment/risk-log/sections/childArea/child-grids';
import RiskCommentsSideBar from '@pages-clean/risk-assessment/risk-log/sections/risk-comments-sidebar';
import RisksGrid from '@pages-clean/risk-assessment/risk-log/sections/risks-grid';
import { busDispatch, useEventBus } from '@utils-clean/eventBus';
import { NullableStudy } from '@contexts/local-state-context';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useBoolean, useTranslation } from '@hooks/index';
import Constants, { SplitterOrientation } from '@utils/constants';
import { getIsActiveField, javascriptDateWithoutTimeFromUTC } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { controlsHubChartConfig } from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROLS_HUB_CHART_CONFIG } from '@queries/risk-assessment';
import Modules from '@config/modules';
import { LoggedInPageProps } from '@config/routes/interfaces';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';
import { useGetOpenRiskReviewCount } from '@apis/bff-api/report-templates';

interface ICTQFactorsWrapperProps {
    study: NullableStudy | undefined;
}

const Content = ({ study }: ICTQFactorsWrapperProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tCommon } = useTranslation('common');
    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '40%', min: '15px' }, { min: '35px' }]);
    const { state, dispatch } = usePageToolbar();
    const user = useAuthorization();
    const showNewDatasetInfoDialog = useBoolean(false);

    const HasCMControlsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.CMControls,
    });

    const HasRiskReportsPermission = user?.can?.({
        module: Constants.Modules.RAM,
        permission: Permissions.Enabled,
        element: Constants.PageElements.RiskReports,
    });

    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const hasCommentsPageAccess = user?.can?.({
        module: Modules.RiskAssessment.value,
        element: Constants.PageElements.RiskLogComments,
        permission: Permissions.Enabled,
    });

    const showCommentsSidebar = state.commentsToggle && hasCommentsPageAccess;

    const hasEither = (HasRiskReportsPermission ?? false) || (HasCMControlsPermission ?? false);

    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules);
    const { data: latestDataset, isLoading: datasetLoading } = ControlsHubApi.useGetLatestActiveDataSet(
        study?.id,
        StudyHasCM2 && hasEither,
    );
    const { data: riskReviewStartDate, loading } = useQuery<controlsHubChartConfig>(GET_CM_CONTROLS_HUB_CHART_CONFIG, {
        variables: { studyId: study?.id },
        skip: !hasEither,
    });

    const { data: openRiskReviewCountData, mutate } = useGetOpenRiskReviewCount(study?.id);

    useEventBus<IRefreshGridEventBusMessage>(REFRESH_GRID, (refresh: boolean) => {
        if (refresh) {
            (async () => {
                await mutate();
            })().catch((err) => window.console.error(err));
        }
    });

    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };

    const [searchParams] = useSearchParams();
    const [includeObsolete, setIncludeObsolete] = useState(() => searchParams.get('includeObsolete') === 'true');
    const [initialTrackedChanges] = useState<FilterUIStateUpdate[] | undefined>(() => {
        const trackedChangesParam = searchParams.get('trackedChanges');
        if (!trackedChangesParam) return undefined;
        try {
            return JSON.parse(trackedChangesParam) as FilterUIStateUpdate[];
        } catch {
            return undefined;
        }
    });

    const includeLtd = initialTrackedChanges?.some((change) => change.fieldKey === 'ltd') ?? false;

    // Deep link /risk-assessment/risk-log/id/:id pre-filters the grid to a single risk. The source
    // screen only knows the recordId, so the filter-ui computes the values to uncheck once the full
    // risk dataset has loaded.
    const params = useParams();
    const initialExactMatch = useMemo(() => {
        const riskId = params.id != null ? Number(params.id) : NaN;
        return Number.isNaN(riskId) ? undefined : { fieldKey: 'recordId', value: riskId };
    }, [params.id]);

    const [selectionState, setSelectionState] = useState<SelectionState>({
        selectedItemsCount: 0,
        bulkUpdateRows: [],
    });

    const [formData, setFormData] = useState<IFormData>({
        risksHaveCmControlsLinked: false,
        study,
        selectionState,
        actionsGridFiltersResetToken: 0,
        actionsGridIsFiltered: false,
        latestDataset: undefined,
        riskReviewStartDate: undefined,
    });

    const riskLogProviderValue = useMemo(
        () => ({
            current: formData,
            setCurrent: setFormData,
            setSelectionState,
        }),
        [formData, setFormData, selectionState],
    );

    useEffect(() => {
        setFormData({
            ...formData,
            study,
            selectionState,
        });
    }, [study?.id, selectionState]);

    useEffect(() => {
        if (openRiskReviewCountData && openRiskReviewCountData > 0 && HasRiskReportsPermission)
            dispatch({
                type: 'setRiskReviewActions',
                riskReviewActions: {
                    content: (
                        <>
                            <Button
                                primary
                                alternativeStyle
                                aria-label={t('riskReviewInProgress')}
                                data-testid="secondary-button"
                                type="button"
                                style={{ marginRight: '12px' }}
                                onClick={() => {
                                    busDispatch<IToggleToolkitEventBusMessage>(
                                        TOGGLE_TOOLKIT,
                                        ToolkitStateValues.HalfSize,
                                    );
                                }}
                                startIcon={
                                    <FontAwesomeIcon
                                        icon={['fal', 'arrows-rotate']}
                                        data-testid="risk-review-in-progress-btn"
                                        size="sm"
                                        style={{ position: 'relative', left: '-5px' }}
                                    />
                                }
                            >
                                {t('riskReviewInProgress')}
                            </Button>
                            {formData.risksHaveCmControlsLinked &&
                                riskReviewStartDate &&
                                riskReviewStartDate?.controlsHubChartConfig &&
                                latestDataset &&
                                latestDataset.dataCutDate &&
                                javascriptDateWithoutTimeFromUTC(latestDataset.dataCutDate!)! >
                                    javascriptDateWithoutTimeFromUTC(
                                        riskReviewStartDate!.controlsHubChartConfig!.cutoffDate,
                                    )! &&
                                HasCMControlsPermission && (
                                    <>
                                        <span style={{ marginRight: '3px' }}>|</span>
                                        <div className={cn('k-d-flex fa-stack')} style={{ display: 'inline-block' }}>
                                            <FontAwesomeIcon
                                                className={cn('fa-stack-1x')}
                                                fontSize={16}
                                                icon={['far', 'database']}
                                            />
                                            <FontAwesomeIcon
                                                fontSize={12}
                                                icon={['fadl', 'circle']}
                                                size="2xs"
                                                className={cn('fa-stack-1x', styles.secondary)}
                                                style={{
                                                    backgroundColor: '#fff',
                                                    borderRadius: '25%',
                                                    padding: '1px 0px',
                                                    position: 'absolute',
                                                    top: '14%',
                                                    left: '57%',
                                                    margin: '0',
                                                    fontSize: '6px',
                                                }}
                                            />
                                        </div>
                                        <Button
                                            primary
                                            aria-label={t('cmControlsRefreshDataset')}
                                            data-testid="secondary-button"
                                            type="button"
                                            style={{ marginRight: '5px' }}
                                            disabled={!canEdit}
                                            onClick={() => {
                                                showNewDatasetInfoDialog.setTrue();
                                            }}
                                        >
                                            {t('cmControlsRefreshDataset')}
                                        </Button>
                                    </>
                                )}
                        </>
                    ),
                },
            });
        else
            dispatch({
                type: 'setRiskReviewActions',
                riskReviewActions: {
                    content: null,
                },
            });
        setFormData({
            ...formData,
            latestDataset:
                latestDataset && latestDataset.dataCutDate
                    ? javascriptDateWithoutTimeFromUTC(latestDataset.dataCutDate!)!
                    : undefined,
            riskReviewStartDate:
                riskReviewStartDate && riskReviewStartDate?.controlsHubChartConfig
                    ? javascriptDateWithoutTimeFromUTC(riskReviewStartDate!.controlsHubChartConfig!.cutoffDate)!
                    : undefined,
        });
    }, [
        latestDataset,
        datasetLoading,
        riskReviewStartDate,
        loading,
        formData.risksHaveCmControlsLinked,
        openRiskReviewCountData,
    ]);

    const onSubmitFetchNewDatasetInfo = (result: string) => {
        if (result === 'success') ShowSuccess(tCommon('actionsSuccessfulToast'));
        else ShowError(tCommon('errorMessage'));
        showNewDatasetInfoDialog.setFalse();
    };

    const includeObsoleteToggle = (
        <Toggle
            data-testid="include-obsolete-toggle"
            checked={includeObsolete}
            onChange={(e) => setIncludeObsolete(e.value)}
        >
            {t('includeObsolete')}
        </Toggle>
    );

    return (
        <div style={{ display: 'grid', gridTemplateColumns: showCommentsSidebar ? 'calc(100% - 430px) auto' : '100%' }}>
            <RiskLogContext.Provider value={riskLogProviderValue}>
                <div style={{ height: 'calc(-128px + 100vh)' }}>
                    <FeActionsBar
                        filterActions
                        headerExtra={includeObsoleteToggle}
                        initialTrackedChanges={initialTrackedChanges}
                        initialExactMatch={initialExactMatch}
                    />
                    <div style={{ height: 'calc(100% - 44px)' }}>
                        <CoreSplitter
                            className="!k-h-full"
                            orientation={SplitterOrientation.vertical}
                            data-testid="risk-log"
                            alignmentValues={panes}
                            onChange={onPaneChange}
                        >
                            <div className="k-h-full">
                                <RisksGrid
                                    includeObsolete={includeObsolete}
                                    includeLtd={includeLtd}
                                    hasInitialFilter={!!initialTrackedChanges?.length || !!initialExactMatch}
                                />
                            </div>
                            <div className="k-h-full">
                                <RiskLogChildAreas />
                            </div>
                        </CoreSplitter>
                    </div>
                </div>
                {showCommentsSidebar && (
                    <div style={{ display: 'flex', height: 'calc(-128px + 100vh)' }}>
                        <RiskCommentsSideBar />
                    </div>
                )}
                {showNewDatasetInfoDialog.value && (
                    <CmControlsNewDatasetInfoDialog
                        onSubmit={onSubmitFetchNewDatasetInfo}
                        onClose={() => showNewDatasetInfoDialog.setFalse()}
                    />
                )}
            </RiskLogContext.Provider>
        </div>
    );
};

const RiskLogWrapper = ({ study }: ICTQFactorsWrapperProps) => {
    const content = <Content study={study} />;

    return <>{content}</>;
};

const RiskLog = ({ study }: LoggedInPageProps) => <RiskLogWrapper study={study} />;

export default RiskLog;
